'use client';
import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Button,
  CircularProgress,
  TextField,
  Stack,
  LinearProgress,
  Chip,
  TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import StorageIcon from '@mui/icons-material/Storage';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import MasterVehicleAddEdit from '../master-data/vehicle/MasterVehicleAddEdit';
import MasterCompanyAddEdit from '../master-data/company/MasterCompanyAddEdit';
import { useCompanyDataList } from '../../stores/companyStore';
import { fetchWaypointReport } from '../../stores/reportStore';
import { useNotifier } from '../../components/Notifier';
import ExcelJS from 'exceljs';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useLanguage } from '../../utils/i18n';
import { timezone } from '../../utils/timeZone';
import WaypointDetailDialog from './WaypointDetailDialog';

const headerStyle = {
  backgroundColor: '#5166AE',
  color: 'white',
  border: '1px solid #ddd',
  padding: '12px 8px',
  textAlign: 'center',
  fontWeight: 'bold',
  whiteSpace: 'nowrap'
};

const cellStyle = {
  border: '1px solid #ddd',
  padding: '8px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  fontSize: '0.875rem'
};

const renderBooleanCell = (val) => {
  const isChecked = val === true || val === 'true' || val === 1 || val === '1' || String(val).toLowerCase() === 'true';
  return isChecked ? <CheckIcon color="success" sx={{ fontSize: 20, verticalAlign: 'middle' }} /> : '-';
};

const getStickyStyle = (leftPos, z, bgColor) => ({
  position: 'sticky',
  left: leftPos,
  zIndex: z,
  backgroundColor: bgColor
});

const stickyCols = {
  action_view: { left: 0, width: 120 },
  date_send: { left: 120, width: 180 },
  wp_time: { left: 300, width: 180 },
  license_plate: { left: 480, width: 120 }
};

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

export default function ReportWayPointPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const notify = useNotifier();

  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [rows, setRows] = useState([]);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Waypoint Detail Dialog State
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [generationTimeRange, setGenerationTimeRange] = useState(false);

  // Filters State
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!periodType) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      } else {
        const adjustedFrom = dayjs(fromDate).second(0).format('YYYY-MM-DD HH:mm:ss');
        const adjustedTo = dayjs(toDate).second(59).format('YYYY-MM-DD HH:mm:ss');
        if (fromDate !== adjustedFrom) setFromDate(adjustedFrom);
        if (toDate !== adjustedTo) setToDate(adjustedTo);
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
      to = now.second(59);
    } else if (periodType === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => selectedVehicles.map((v) => String(v.value)), [selectedVehicles]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          t('reportModule.unknownCompany', 'Unknown Company'),
        raw: c.raw ?? c
      })),
    [vehicleList, t]
  );

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('reportModule.errMaxRange31Days', 'Maximum time span of 31 days'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('reports.errStartTimeLater', 'Waktu awal tidak boleh melebihi waktu akhir'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handleFetchData = async () => {
    if (selectedCompanyIds.length === 0) {
      notify({ message: t('reports.errSelectCompany', 'Please select a company'), severity: 'warning' });
      return;
    }
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('reports.errSelectVehicle', 'Please select a vehicle'), severity: 'warning' });
      return;
    }
    if (!fromDate || !toDate || dateError) {
      notify({ message: t('reports.errSelectValidDateRange', 'Please select a valid date range'), severity: 'warning' });
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    setLoadingText(t('reportModule.preparingData', 'Preparing data...'));
    try {
      let apiChunks = [];
      const localStart = dayjs(fromDate).second(0);
      const localEnd = dayjs(toDate).second(59);
      const pType = periodType;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          apiChunks.push({
            startLocal: curr.startOf('day'),
            endLocal: curr.endOf('day'),
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            startLocal: chunkStart,
            endLocal: chunkEnd,
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });

          curr = curr.add(1, 'day');
        }
      }

      const allData = [];
      const totalVehicles = selectedVehicleIds.length;
      let completedVehicles = 0;
      const chunkSize = 5;

      for (let i = 0; i < totalVehicles; i += chunkSize) {
        const chunk = selectedVehicleIds.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (vId) => {
            const vInfo =
              selectedVehicles.find((o) => String(o.value) === String(vId)) ||
              vehicleOptionsNormalized.find((o) => String(o.value) === String(vId));
            const vehicleCompanyId = vInfo?.raw?.company || vInfo?.original?.company_id || vInfo?.original?.company || 0;
            const licensePlate = vInfo?.label || String(vId);

            let vehicleDailyData = [];

            const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
            for (const chunk of apiChunks) {
              const dStartIso = dayjs(chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'));
              const dEndIso = dayjs(chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'));

              try {
                const res = await fetchWaypointReport(vehicleCompanyId, vId, dStartIso, dEndIso, tz);
                if (Array.isArray(res)) vehicleDailyData = vehicleDailyData.concat(res);
                else if (res?.data && Array.isArray(res.data)) vehicleDailyData = vehicleDailyData.concat(res.data);
              } catch (err) {
                console.error(`Error fetching waypoint data for ${licensePlate} on ${dStartIso.format()}`, err);
              }
            }

            completedVehicles++;
            setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
            setLoadingText(
              `${t('reportModule.processingUnit', 'Processing unit:')} ${licensePlate} (${completedVehicles}/${totalVehicles})`
            );
            return vehicleDailyData;
          })
        );
        allData.push(...chunkResults.flat());
      }

      const data = allData;
      if (data && data.length > 0) {
        setRows(
          data.map((item, index) => ({
            ...item,
            virtual_odo: item.virtual_odo != null ? (item.virtual_odo / 1000).toFixed(2) : null,
            total_odo: item.total_odo != null ? (item.total_odo / 1000).toFixed(2) : null,
            id: index
          }))
        );
        setGenerationTimeRange(true);
        notify({ message: t('reports.dataFetchedSuccess', 'Data fetched successfully'), severity: 'success' });
      } else {
        setRows([]);
        setGenerationTimeRange(false);
        notify({ message: t('reports.noDataFound', 'No data found for the selected period'), severity: 'info' });
      }
    } catch (error) {
      console.error(error);
      notify({ message: t('reports.errFetchData', 'Failed to fetch data'), severity: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!rows || rows.length === 0) {
      notify({ message: t('reports.noDataExport', 'Tidak ada data untuk diexport'), severity: 'warning' });
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(t('reportModule.waypointData', 'Waypoint Data'));

      // Parse CANBUS for each row to extract all unique field keys
      const parsedCanbusList = rows.map((row) => {
        if (!row.canbus) return {};
        try {
          const rawData = row.canbus.data !== undefined ? row.canbus.data : row.canbus;
          let parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          if (typeof parsed === 'string') {
            try {
              parsed = JSON.parse(parsed);
            } catch {
              // keep as is
            }
          }
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed;
          }
          return typeof rawData === 'string' ? { canbus: rawData } : {};
        } catch {
          return typeof row.canbus === 'string' ? { canbus: row.canbus } : {};
        }
      });

      const canbusKeySet = new Set();
      parsedCanbusList.forEach((cb) => {
        Object.keys(cb).forEach((k) => canbusKeySet.add(k));
      });
      const canbusKeys = Array.from(canbusKeySet);

      const baseHeaders = [
        t('reports.dateSend', 'Date Send'),
        t('reports.wpTime', 'WP Time'),
        'IMEI No',
        t('reports.licensePlate', 'License Plate'),
        'Lon',
        'Lat',
        t('reports.street', 'Street'),
        t('reports.speed', 'Speed'),
        t('reports.angle', 'Angle'),
        t('reportModule.satelites', 'Satelites'),
        'Din 1 Desc',
        'Din 1',
        'Din 2 Desc',
        'Din 2',
        'Din 3 Desc',
        'Din 3',
        'Din 4 Desc',
        'Din 4',
        'Din 5 Desc',
        'Din 5',
        'Ain 1',
        'Ain 2',
        'Ain 3',
        'Ain 4',
        'Ain 5',
        t('reports.virtualOdo', 'Virtual Odo'),
        t('reports.totalOdo', 'Total Odo'),
        t('reports.powerVolt', 'Power Volt'),
        t('reports.batteryVolt', 'Battery Volt'),
        t('reports.deepSleep', 'Deep Sleep'),
        t('reports.harshAcceleration', 'Harsh Acceleration'),
        t('exportWaypoint.harshBreaking', 'Harsh Breaking'),
        t('reports.liter', 'Liter'),
        t('reportModule.pitNamePoolName', 'PIT Name (pool_name)'),
        t('reportModule.jettyNameTransitPointName', 'Jetty Name (transit_point_name)'),
        t('reports.geofenceName', 'Geofence Name'),
        t('reports.routeName', 'Route Name'),
        t('reports.suhu1', 'Suhu 1'),
        t('reports.suhu2', 'Suhu 2'),
        t('reports.suhu3', 'Suhu 3'),
        t('reports.suhu4', 'Suhu 4'),
        t('reports.suhu5', 'Suhu 5'),
        t('reports.suhu6', 'Suhu 6'),
        t('reports.suhu7', 'Suhu 7'),
        t('reports.suhu8', 'Suhu 8'),
        t('reports.movement', 'Movement'),
        t('reportModule.gsmSignal', 'Gsm Signal'),
        t('reports.acceleration', 'Acceleration'),
        t('reports.altitude', 'Altitude'),
        'RFID'
      ];

      const headers = [...baseHeaders, ...(canbusKeys.length > 0 ? canbusKeys : [t('reports.canbusDetails', 'CANBUS Details')])];

      sheet.addRow(headers);
      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5166AE' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 25;

      rows.forEach((row, index) => {
        const canbusObj = parsedCanbusList[index] || {};
        const canbusValues =
          canbusKeys.length > 0
            ? canbusKeys.map((k) => {
                const val = canbusObj[k];
                if (val === undefined || val === null) return '';
                if (typeof val === 'object') return JSON.stringify(val);
                return val;
              })
            : [''];

        sheet.addRow([
          row.date_send && dayjs(row.date_send).isValid() ? dayjs(row.date_send).format('DD/MM/YYYY HH:mm:ss') : row.date_send || '-',
          row.wp_time && dayjs(row.wp_time).isValid() ? dayjs(row.wp_time).format('DD/MM/YYYY HH:mm:ss') : row.wp_time || '-',
          row.imei_no,
          row.license_plate,
          row.lon,
          row.lat,
          row.street,
          row.speed,
          row.angle,
          row.satellites,
          row.din1_desc,
          row.din1,
          row.din2_desc,
          row.din2,
          row.din3_desc,
          row.din3,
          row.din4_desc,
          row.din4,
          row.din5_desc,
          row.din5,
          row.ain1,
          row.ain2,
          row.ain3,
          row.ain4,
          row.ain5,
          row.virtual_odo,
          row.total_odo,
          row.power_volt,
          row.batt_volt,
          row.deep_sleep,
          row.harsh_acceleration,
          row.harsh_breaking,
          row.liter,
          row.pool_name,
          row.transit_point_name,
          row.geofence_name,
          row.route_name,
          row.temperature,
          row.temperature1,
          row.temperature2,
          row.temperature3,
          row.temperature4,
          row.temperature5,
          row.temperature6,
          row.temperature7,
          row.movement,
          row.gsm_signal,
          row.acceleration,
          row.altitude,
          row.rfid,
          ...canbusValues
        ]);
      });

      sheet.columns.forEach((column) => {
        let maxLen = 10;
        column.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.value !== null && cell.value !== undefined) {
            const valStr = typeof cell.value === 'object' && cell.value.text ? cell.value.text : cell.value.toString();
            const valLen = valStr.length;
            if (valLen > maxLen) maxLen = valLen;
          }
        });
        column.width = Math.min(maxLen + 2, 50); // limit max width
      });

      sheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD0D7DE' } },
            left: { style: 'thin', color: { argb: 'FFD0D7DE' } },
            bottom: { style: 'thin', color: { argb: 'FFD0D7DE' } },
            right: { style: 'thin', color: { argb: 'FFD0D7DE' } }
          };
          if (rowNumber > 1) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          }
        });
      });

      sheet.views = [{ state: 'frozen', xSplit: 3, ySplit: 1, topLeftCell: 'D2' }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Waypoint_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      notify({ message: t('reports.errExportExcel', 'Failed to export to Excel'), severity: 'error' });
    }
  };

  const filteredRows = useMemo(() => {
    let result = rows;
    if (tableSearchQuery) {
      const lowerQ = tableSearchQuery.toLowerCase();
      result = result.filter((row) => (row.license_plate || '').toLowerCase().includes(lowerQ));
    }
    return result;
  }, [rows, tableSearchQuery]);

  const columns = useMemo(
    () => [
      {
        field: 'action_view',
        headerName: t('general.action', 'Action'),
        width: 120,
        renderCell: (params) => {
          return (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                const idx = filteredRows.findIndex((r) => r.id === params.row.id);
                setSelectedRowIndex(idx >= 0 ? idx : 0);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 'bold',
                boxShadow: 'none',
                px: 2
              }}
              startIcon={<InfoOutlinedIcon />}
            >
              {t('reportFatigue.details', 'Details')}
            </Button>
          );
        }
      },
      {
        field: 'date_send',
        headerName: t('reports.dateSend', 'Date Send'),
        width: 180,
        renderCell: (params) => (params.row.date_send ? dayjs(params.row.date_send).format('DD/MM/YYYY HH:mm:ss') : '-')
      },
      {
        field: 'wp_time',
        headerName: t('reports.wpTime', 'WP Time'),
        width: 180,
        renderCell: (params) => (params.row.wp_time ? dayjs(params.row.wp_time).format('DD/MM/YYYY HH:mm:ss') : '-')
      },
      { field: 'license_plate', headerName: t('reports.licensePlate', 'License Plate'), width: 120 },
      { field: 'lon', headerName: t('reports.lon', 'Lon'), width: 100 },
      { field: 'lat', headerName: t('reports.lat', 'Lat'), width: 100 },
      {
        field: 'street',
        headerName: t('reports.street', 'Street'),
        width: 250,
        renderCell: (params) => {
          const { lat, lon, street } = params.row || {};
          if (lat && lon && street) {
            return (
              <a
                href={`http://maps.google.com/maps?z=18&q=${lat},${lon}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'underline', color: '#1976d2' }}
              >
                {street}
              </a>
            );
          }
          return street || '-';
        }
      },
      { field: 'speed', headerName: t('reports.speed', 'Speed'), width: 90 },
      { field: 'angle', headerName: t('reports.angle', 'Angle'), width: 90 },
      { field: 'satellites', headerName: t('reports.satellites', 'Satelites'), width: 100 },
      { field: 'din1_desc', headerName: t('reports.din1Desc', 'Din 1 Desc'), width: 120 },
      {
        field: 'din1',
        headerName: t('reports.din1', 'Din 1'),
        width: 80,
        type: 'boolean',
        renderCell: (params) => renderBooleanCell(params.row.din1)
      },
      { field: 'din2_desc', headerName: t('reports.din2Desc', 'Din 2 Desc'), width: 120 },
      {
        field: 'din2',
        headerName: t('reports.din2', 'Din 2'),
        width: 80,
        type: 'boolean',
        renderCell: (params) => renderBooleanCell(params.row.din2)
      },
      { field: 'din3_desc', headerName: t('reports.din3Desc', 'Din 3 Desc'), width: 120 },
      {
        field: 'din3',
        headerName: t('reports.din3', 'Din 3'),
        width: 80,
        type: 'boolean',
        renderCell: (params) => renderBooleanCell(params.row.din3)
      },
      { field: 'din4_desc', headerName: t('reports.din4Desc', 'Din 4 Desc'), width: 120 },
      {
        field: 'din4',
        headerName: t('reports.din4', 'Din 4'),
        width: 80,
        type: 'boolean',
        renderCell: (params) => renderBooleanCell(params.row.din4)
      },
      { field: 'din5_desc', headerName: t('reports.din5Desc', 'Din 5 Desc'), width: 120 },
      {
        field: 'din5',
        headerName: t('reports.din5', 'Din 5'),
        width: 80,
        type: 'boolean',
        renderCell: (params) => renderBooleanCell(params.row.din5)
      },
      { field: 'ain1', headerName: t('reports.ain1', 'Ain 1'), width: 80 },
      { field: 'ain2', headerName: t('reports.ain2', 'Ain 2'), width: 80 },
      { field: 'ain3', headerName: t('reports.ain3', 'Ain 3'), width: 80 },
      { field: 'ain4', headerName: t('reports.ain4', 'Ain 4'), width: 80 },
      { field: 'ain5', headerName: t('reports.ain5', 'Ain 5'), width: 80 },
      { field: 'virtual_odo', headerName: t('reports.virtualOdo', 'Virtual Odo'), width: 110 },
      { field: 'total_odo', headerName: t('reports.totalOdo', 'Total Odo'), width: 110 },
      { field: 'power_volt', headerName: t('reports.powerVolt', 'Power Volt'), width: 110 },
      { field: 'batt_volt', headerName: t('reports.batteryVolt', 'Battery Volt'), width: 110 },
      { field: 'deep_sleep', headerName: t('reports.deepSleep', 'Deep Sleep'), width: 110 },
      { field: 'harsh_acceleration', headerName: t('reports.harshAcceleration', 'Harsh Acceleration'), width: 150 },
      { field: 'harsh_breaking', headerName: t('reports.harshBraking', 'Harsh Breaking'), width: 140 },
      { field: 'liter', headerName: t('reports.liter', 'Liter'), width: 90 },
      { field: 'pool_name', headerName: t('reports.pitName', 'PIT Name'), width: 150 },
      { field: 'transit_point_name', headerName: t('reports.jettyName', 'Jetty Name'), width: 150 },
      { field: 'geofence_name', headerName: t('reports.geofenceName', 'Geofence Name'), width: 150 },
      { field: 'route_name', headerName: t('reports.routeName', 'Route Name'), width: 150 },
      { field: 'temperature', headerName: t('reports.suhu1', 'Suhu 1'), width: 90 },
      { field: 'temperature1', headerName: t('reports.suhu2', 'Suhu 2'), width: 90 },
      { field: 'temperature2', headerName: t('reports.suhu3', 'Suhu 3'), width: 90 },
      { field: 'temperature3', headerName: t('reports.suhu4', 'Suhu 4'), width: 90 },
      { field: 'temperature4', headerName: t('reports.suhu5', 'Suhu 5'), width: 90 },
      { field: 'temperature5', headerName: t('reports.suhu6', 'Suhu 6'), width: 90 },
      { field: 'temperature6', headerName: t('reports.suhu7', 'Suhu 7'), width: 90 },
      { field: 'temperature7', headerName: t('reports.suhu8', 'Suhu 8'), width: 90 },
      { field: 'movement', headerName: t('reports.movement', 'Movement'), width: 100 },
      { field: 'gsm_signal', headerName: t('reports.gsmSignal', 'Gsm Signal'), width: 110 },
      { field: 'acceleration', headerName: t('reports.acceleration', 'Acceleration'), width: 110 },
      { field: 'altitude', headerName: t('reports.altitude', 'Altitude'), width: 100 },
      { field: 'rfid', headerName: t('reports.rfid', 'RFID'), width: 100 },
      {
        field: 'canbus',
        headerName: t('reports.canbus', 'CANBUS'),
        width: 150,
        renderCell: (params) => {
          const canbus = params.row.canbus;
          if (!canbus) return '-';
          return (
            <Chip
              label={t('reportModule.available', 'Available')}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          );
        }
      }
    ],
    [t, filteredRows]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <StorageIcon fontSize="large" color="primary" />
            {t('reports.reportsWaypoint', 'Report Waypoint')}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            flexShrink: 0,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
          }}
        >
          <Box
            sx={{
              p: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2
            }}
          >
            {/* <InfoOutlinedIcon sx={{ color: '#1976d2', fontSize: 20, mt: 0.2 }} /> */}
            <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
              {t(
                'reports.sensorWaypointDesc',
                'Comprehensive data of vehicle location history, including telemetry, sensor status, temperature, and Canbus details.'
              )}
            </Typography>
            {(localStorage.getItem('rolename') === 'Administrator' ||
              (() => {
                try {
                  return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes(
                    '"modules_path":"report-catalog"'
                  );
                } catch {
                  return false;
                }
              })()) && (
              <Box>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/report-catalog?name=report-catalog')}
                  size="small"
                  variant="contained"
                  sx={{
                    bgcolor: 'primary',
                    color: '#ffffff',
                    textTransform: 'none',
                    px: 0.5,
                    py: 0,
                    minWidth: 0,
                    '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                  }}
                >
                  {t('reports.reportCatalog', 'Report Catalog')}
                </Button>
              </Box>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label={t('general.company', 'Company')}
                value={selectedCompanyIds}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicles([]);
                }}
                allowAdd
                AddComponent={MasterCompanyAddEdit}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                multiSelect={true}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <PickerField
                label={t('reports.selectVehicle', 'Select Vehicle')}
                value={selectedVehicleIds[0] || ''}
                selectedItems={selectedVehicles}
                {...vehiclePickerParams}
                options={vehicleOptionsNormalized}
                allowAdd
                AddComponent={MasterVehicleAddEdit}
                onChange={(val) => {
                  if (!val) {
                    setSelectedVehicles([]);
                    return;
                  }
                  const strId = String(val);
                  let match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
                  if (!match) match = selectedVehicles.find((v) => String(v.value) === strId);

                  if (!match) {
                    const rawMatch =
                      (vehicleList || []).find((v) => String(v.value) === strId) ||
                      (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
                    if (rawMatch) {
                      match = {
                        value: strId,
                        label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
                        companyName: rawMatch.companyName,
                        raw: rawMatch.raw
                      };
                    } else {
                      match = { value: strId, label: strId };
                    }
                  }
                  setSelectedVehicles([match]);
                }}
                dialogTitle={t('reports.selectVehicle', 'Select Vehicle')}
                multiSelect={false}
              />
            </Box>
            <Box sx={{ flex: '0 1 180px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="period-label">{t('reports.selectPeriod', 'Pilih Periode')}</InputLabel>
                <MuiSelect
                  labelId="period-label"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  disabled={loading}
                  input={<OutlinedInput label={t('reports.selectPeriod', 'Pilih Periode')} />}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="today">{t('mapsTracking.today', 'Today')}</MenuItem>
                  <MenuItem value="week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                  <MenuItem value="custom">{t('reports.custom', 'Custom')}</MenuItem>
                </MuiSelect>
              </FormControl>
            </Box>

            {periodType === 'custom' && (
              <Box sx={{ flex: '1 1 350px' }}>
                <Stack direction="row" spacing={2}>
                  <DateTimePicker
                    ampm={false}
                    label={t('reports.from', 'From')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm:ss"
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(0).format('YYYY-MM-DD HH:mm:ss') : '';
                      setFromDate(newV);
                      validateDateRange(dayjs(newV), dayjs(toDate));
                    }}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                  <DateTimePicker
                    ampm={false}
                    label={t('reports.to', 'To')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm:ss"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(59).format('YYYY-MM-DD HH:mm:ss') : '';
                      setToDate(newV);
                      validateDateRange(dayjs(fromDate), dayjs(newV));
                    }}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                </Stack>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', mt: { xs: 2, md: 0 } }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportExcel}
                disabled={loading || rows.length === 0}
                sx={{ px: 3, fontWeight: 'bold', boxShadow: 2, bgcolor: '#ffffff' }}
              >
                {t('general.export', 'Export')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleFetchData}
                disabled={loading || !!dateError || selectedVehicleIds.length === 0}
                sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
              >
                {loading ? t('dashboardFuel.generating', 'Generating...') : t('dashboardFuel.generate', 'Generate')}
              </Button>
            </Box>
          </Box>
          {dateError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {dateError}
            </Typography>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #eee', pt: 2 }}>
            <TextField
              size="small"
              placeholder={t('reports.searchLicensePlate', 'Search License Plate')}
              value={tableSearchQuery}
              onChange={(e) => setTableSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 250, bgcolor: '#ffffff' }}
            />
          </Box>
        </Paper>

        {generationTimeRange && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1,
              flexShrink: 0,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: 'rgba(72, 93, 154, 0.05)',
              border: '1px solid rgba(72, 93, 154, 0.2)',
              boxShadow: '0 2px 8px rgba(72, 93, 154, 0.05)'
            }}
          >
            <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
            <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
              {t('revenue.showingDataFrom', 'Showing data from')}{' '}
              <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
              </Box>{' '}
              {t('revenue.to', 'to')}{' '}
              <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
              </Box>
            </Typography>
          </Box>
        )}

        {loading && loadingProgress > 0 && (
          <Box
            sx={{
              p: 2,
              textAlign: 'center',
              bgcolor: '#fff',
              borderRadius: 2,
              mb: 1,
              flexShrink: 0,
              border: '1px dashed rgba(72, 93, 154, 0.3)'
            }}
          >
            <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1.5,
                  bgcolor: 'rgba(72, 93, 154, 0.2)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                }}
              />
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                {loadingProgress}% - {loadingText}
              </Typography>
            </Box>
          </Box>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 200,
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              minHeight: 0,
              overflowX: 'auto',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: 1
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 2000 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr>
                  {columns.map((col) => {
                    const isSticky = stickyCols[col.field];
                    const customStyle = isSticky
                      ? {
                          ...headerStyle,
                          ...getStickyStyle(isSticky.left, 4, '#5166AE'),
                          minWidth: isSticky.width,
                          maxWidth: isSticky.width,
                          boxSizing: 'border-box'
                        }
                      : headerStyle;
                    return (
                      <th key={col.field} style={customStyle}>
                        {col.headerName}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} style={{ ...cellStyle, padding: '20px' }}>
                      <CircularProgress />
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} style={{ ...cellStyle, padding: '20px' }}>
                      {t('reports.noDataFound', 'No data found')}
                    </td>
                  </tr>
                ) : (
                  filteredRows
                    .slice(
                      paginationModel.page * paginationModel.pageSize,
                      paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
                    )
                    .map((row) => (
                      <tr key={row.id}>
                        {columns.map((col) => {
                          const isSticky = stickyCols[col.field];
                          const customStyle = isSticky
                            ? {
                                ...cellStyle,
                                ...getStickyStyle(isSticky.left, 1, '#fff'),
                                minWidth: isSticky.width,
                                maxWidth: isSticky.width,
                                boxSizing: 'border-box'
                              }
                            : cellStyle;
                          let val = row[col.field];
                          if (col.renderCell) {
                            val = col.renderCell({ row });
                          } else if (typeof val === 'boolean' || col.type === 'boolean') {
                            val = renderBooleanCell(val);
                          }
                          return (
                            <td key={col.field} style={customStyle}>
                              {val}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e0e0e0',
              bgcolor: 'background.paper',
              zIndex: 1,
              pt: 0.5
            }}
          >
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={paginationModel.page}
              onPageChange={(e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: 48,
                  px: 1
                }
              }}
            />
          </Box>
        </Paper>

        <WaypointDetailDialog
          open={selectedRowIndex !== null}
          onClose={() => setSelectedRowIndex(null)}
          dataList={filteredRows}
          initialIndex={selectedRowIndex}
        />
      </Box>
    </LocalizationProvider>
  );
}
